// pages/Migracion.jsx
import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';
import { Link } from 'react-router-dom';

// ─── SISTEMAS ────────────────────────────────────────────────────────────────

const SISTEMAS = [
  { key: 'quickbooks_desktop', label: 'QuickBooks Desktop', logo: '🟦', pais: 'Universal' },
  { key: 'quickbooks_online',  label: 'QuickBooks Online',  logo: '🟩', pais: 'Universal' },
  { key: 'xero',               label: 'Xero',               logo: '🔵', pais: 'Universal' },
  { key: 'sage',               label: 'Sage 50 / 100',      logo: '🟢', pais: 'Universal' },
  { key: 'sap_b1',             label: 'SAP Business One',   logo: '🔷', pais: 'Universal' },
  { key: 'odoo',               label: 'Odoo',               logo: '🟣', pais: 'Universal' },
  { key: 'contpaq',            label: 'Contpaq',            logo: '🇲🇽', pais: 'México / LatAm' },
  { key: 'saint',              label: 'SAINT',              logo: '🇻🇪', pais: 'Venezuela / Caribe' },
  { key: 'excel_generico',     label: 'Excel / CSV genérico', logo: '📊', pais: 'Universal' },
];

// ─── ENTIDADES ───────────────────────────────────────────────────────────────

const ENTIDADES_CONFIG = {
  clientes: {
    label: 'Clientes', icon: '👥',
    campos: [
      { key: 'nombre',            label: 'Nombre / Razón Social',   required: true  },
      { key: 'rnc_cedula',        label: 'RNC / Cédula / Tax ID',   required: false },
      { key: 'email',             label: 'Email',                   required: false },
      { key: 'telefono',          label: 'Teléfono',                required: false },
      { key: 'direccion',         label: 'Dirección',               required: false },
      { key: 'balance_pendiente', label: 'Saldo Pendiente',         required: false },
      { key: 'limite_credito',    label: 'Límite de Crédito',       required: false },
    ],
  },
  proveedores: {
    label: 'Proveedores', icon: '🏭',
    campos: [
      { key: 'nombre',    label: 'Nombre / Razón Social',   required: true  },
      { key: 'rnc',       label: 'RNC / Tax ID',            required: false },
      { key: 'email',     label: 'Email',                   required: false },
      { key: 'telefono',  label: 'Teléfono',                required: false },
      { key: 'direccion', label: 'Dirección',               required: false },
      { key: 'contacto',  label: 'Persona de Contacto',     required: false },
    ],
  },
  productos: {
    label: 'Productos / Inventario', icon: '📦',
    campos: [
      { key: 'codigo',                 label: 'Código',              required: false },
      { key: 'nombre',                 label: 'Nombre',              required: true  },
      { key: 'descripcion',            label: 'Descripción',         required: false },
      { key: 'unidad',                 label: 'Unidad de Medida',    required: false },
      { key: 'precio_venta',           label: 'Precio de Venta',     required: false },
      { key: 'costo_unitario_inicial', label: 'Costo Unitario',      required: false },
      { key: 'stock_actual',           label: 'Existencia Actual',   required: false },
      { key: 'stock_minimo',           label: 'Stock Mínimo',        required: false },
    ],
  },
  plan_cuentas: {
    label: 'Plan de Cuentas', icon: '📋',
    campos: [
      { key: 'codigo',      label: 'Código de Cuenta',                               required: true  },
      { key: 'nombre',      label: 'Nombre de la Cuenta',                            required: true  },
      { key: 'tipo',        label: 'Tipo  (activo|pasivo|capital|ingreso|egreso|costo)', required: true  },
      { key: 'descripcion', label: 'Descripción',                                    required: false },
    ],
  },
};

// ─── MAPEOS POR SISTEMA ───────────────────────────────────────────────────────
// Cada campo contiene array de posibles nombres de columna en ese sistema (orden de prioridad)

const SISTEMAS_MAPEOS = {
  quickbooks_desktop: {
    clientes: {
      nombre:            ['Customer', 'Company Name', 'Full Name', 'Name', 'Customer Name'],
      rnc_cedula:        ['Tax ID', 'Social Security No.', 'Resale No.', 'Tax Identification'],
      email:             ['Main Email', 'Email', 'E-mail'],
      telefono:          ['Main Phone', 'Phone', 'Telephone'],
      direccion:         ['Bill to 1', 'Billing Address Line 1', 'Address', 'Street'],
      balance_pendiente: ['Balance Total', 'Current Balance', 'Open Balance', 'Balance'],
      limite_credito:    ['Credit Limit'],
    },
    proveedores: {
      nombre:    ['Vendor', 'Company Name', 'Full Name', 'Name', 'Vendor Name'],
      rnc:       ['Tax ID', 'Social Security No.', 'Tax Identification'],
      email:     ['Main Email', 'Email', 'E-mail'],
      telefono:  ['Main Phone', 'Phone'],
      direccion: ['Address 1', 'Bill Address 1', 'Address', 'Street'],
      contacto:  ['Contact Name', 'First Name', 'Last Name'],
    },
    productos: {
      codigo:                 ['Item Name', 'Item', 'Item Number', 'Item Code'],
      nombre:                 ['Description', 'Sales Description', 'Item Description'],
      descripcion:            ['Purchase Description', 'Description', 'Notes'],
      unidad:                 ['U/M', 'Unit', 'Unit of Measure'],
      precio_venta:           ['Sales Price', 'Price', 'Rate'],
      costo_unitario_inicial: ['Cost', 'Purchase Cost', 'Avg Cost'],
      stock_actual:           ['Quantity On Hand', 'On Hand', 'QOH'],
      stock_minimo:           ['Reorder Pt', 'Reorder Point', 'Min Quantity'],
    },
    plan_cuentas: {
      codigo:      ['Acct. #', 'Account #', 'Account Number', 'Num.', 'Number'],
      nombre:      ['Account', 'Account Name', 'Name'],
      tipo:        ['Type', 'Account Type'],
      descripcion: ['Description', 'Note'],
    },
  },

  quickbooks_online: {
    clientes: {
      nombre:            ['Display Name', 'Company', 'Name', 'Customer Name'],
      rnc_cedula:        ['Tax Reg. No.', 'Business ID No.', 'Tax ID'],
      email:             ['Email', 'Main Email'],
      telefono:          ['Phone', 'Mobile'],
      direccion:         ['Billing Street', 'Street', 'Address'],
      balance_pendiente: ['Open Balance', 'Balance'],
      limite_credito:    ['Credit Limit'],
    },
    proveedores: {
      nombre:    ['Display Name', 'Company', 'Name', 'Vendor Name'],
      rnc:       ['Tax Reg. No.', 'Business ID No.'],
      email:     ['Email', 'Main Email'],
      telefono:  ['Phone', 'Mobile'],
      direccion: ['Street', 'Address', 'Billing Street'],
      contacto:  ['Contact Name', 'First Name'],
    },
    productos: {
      codigo:                 ['SKU', 'Name', 'Product/Service'],
      nombre:                 ['Name', 'Product/Service', 'Description'],
      descripcion:            ['Description', 'Sales Description'],
      precio_venta:           ['Sales Price/Rate', 'Rate', 'Price'],
      costo_unitario_inicial: ['Cost', 'Purchase Cost'],
      stock_actual:           ['Qty On Hand', 'Quantity', 'On Hand'],
      stock_minimo:           ['Reorder Point'],
    },
    plan_cuentas: {
      codigo:      ['#', 'Account #', 'Number'],
      nombre:      ['Account', 'Name'],
      tipo:        ['Type', 'Account Type', 'Detail Type'],
      descripcion: ['Description'],
    },
  },

  xero: {
    clientes: {
      nombre:            ['Name', 'Contact Name', 'Company'],
      rnc_cedula:        ['Tax Number', 'Registration Number'],
      email:             ['Email Address', 'Email'],
      telefono:          ['Phone', 'Telephone'],
      direccion:         ['Street', 'Address Line 1'],
      balance_pendiente: ['Outstanding Balance', 'Balance'],
    },
    proveedores: {
      nombre:    ['Name', 'Contact Name', 'Company'],
      rnc:       ['Tax Number', 'Registration Number'],
      email:     ['Email Address', 'Email'],
      telefono:  ['Phone', 'Telephone'],
      direccion: ['Street', 'Address Line 1'],
      contacto:  ['First Name', 'Contact Name'],
    },
    productos: {
      codigo:                 ['Item Code', 'Code'],
      nombre:                 ['Item Name', 'Name', 'Description'],
      descripcion:            ['Description', 'Sales Description'],
      precio_venta:           ['Unit Price', 'Sales Price'],
      costo_unitario_inicial: ['Cost Price', 'Purchase Price'],
      stock_actual:           ['Quantity', 'On Hand'],
    },
    plan_cuentas: {
      codigo:      ['Code', 'Account Code'],
      nombre:      ['Name', 'Account Name'],
      tipo:        ['Type', 'Account Type'],
      descripcion: ['Description'],
    },
  },

  sage: {
    clientes: {
      nombre:            ['Customer Name', 'Name', 'Account Name'],
      rnc_cedula:        ['Tax ID', 'Federal Tax ID'],
      email:             ['E-mail', 'Email'],
      telefono:          ['Phone 1', 'Telephone'],
      direccion:         ['Address 1', 'Street'],
      balance_pendiente: ['Current Balance', 'Balance', 'Amount Due'],
      limite_credito:    ['Credit Limit'],
    },
    proveedores: {
      nombre:    ['Vendor Name', 'Name', 'Account Name'],
      rnc:       ['Tax ID', 'Federal Tax ID'],
      email:     ['E-mail', 'Email'],
      telefono:  ['Phone 1', 'Telephone'],
      direccion: ['Address 1', 'Street'],
      contacto:  ['Contact Name', 'Contact'],
    },
    productos: {
      codigo:                 ['Item ID', 'Code', 'Number'],
      nombre:                 ['Item Description', 'Description', 'Name'],
      unidad:                 ['Unit/Measure', 'UOM'],
      precio_venta:           ['Price Level 1', 'Unit Price', 'Sales Price'],
      costo_unitario_inicial: ['Last Unit Cost', 'Standard Cost', 'Cost'],
      stock_actual:           ['Quantity on Hand', 'In Stock'],
      stock_minimo:           ['Minimum Stock', 'Reorder Quantity'],
    },
    plan_cuentas: {
      codigo:      ['Account ID', 'Number'],
      nombre:      ['Account Description', 'Name'],
      tipo:        ['Account Type', 'Type'],
      descripcion: ['Description'],
    },
  },

  sap_b1: {
    clientes: {
      nombre:            ['CardName', 'Business Partner Name'],
      rnc_cedula:        ['TaxNumber', 'Federal Tax ID'],
      email:             ['EmailAddress', 'E-Mail'],
      telefono:          ['Phone1', 'Phone Number'],
      direccion:         ['Address', 'Street'],
      balance_pendiente: ['Balance', 'Total Receivable'],
      limite_credito:    ['CreditLimit', 'Credit Limit'],
    },
    proveedores: {
      nombre:    ['CardName', 'Business Partner Name'],
      rnc:       ['TaxNumber', 'Federal Tax ID'],
      email:     ['EmailAddress', 'E-Mail'],
      telefono:  ['Phone1', 'Phone Number'],
      direccion: ['Address', 'Street'],
      contacto:  ['ContactPerson', 'Contact'],
    },
    productos: {
      codigo:                 ['ItemCode', 'Item No.'],
      nombre:                 ['ItemName', 'Description'],
      descripcion:            ['UserText', 'Remarks'],
      unidad:                 ['BuyUnitMsr', 'UoM'],
      precio_venta:           ['SalesPrice', 'Price'],
      costo_unitario_inicial: ['AvgPrice', 'Standard Price'],
      stock_actual:           ['OnHand', 'In Stock'],
      stock_minimo:           ['MinInvent', 'Min Inventory'],
    },
    plan_cuentas: {
      codigo:      ['AcctCode', 'Account Code'],
      nombre:      ['AcctName', 'Account Name'],
      tipo:        ['AcctType', 'Account Type'],
      descripcion: ['Notes', 'Remarks'],
    },
  },

  odoo: {
    clientes: {
      nombre:            ['Name', 'Contact Name', 'Company Name'],
      rnc_cedula:        ['VAT', 'Tax ID'],
      email:             ['Email', 'E-Mail'],
      telefono:          ['Phone', 'Mobile'],
      direccion:         ['Street', 'Address'],
      balance_pendiente: ['Total Receivable', 'Credit', 'Balance'],
      limite_credito:    ['Credit Limit'],
    },
    proveedores: {
      nombre:    ['Name', 'Contact Name', 'Vendor'],
      rnc:       ['VAT', 'Tax ID'],
      email:     ['Email'],
      telefono:  ['Phone', 'Mobile'],
      direccion: ['Street', 'Address'],
      contacto:  ['Contact Name'],
    },
    productos: {
      codigo:                 ['Internal Reference', 'Product Code', 'Default Code'],
      nombre:                 ['Product Name', 'Name'],
      descripcion:            ['Description', 'Internal Notes'],
      unidad:                 ['Unit of Measure', 'UoM'],
      precio_venta:           ['Sales Price', 'Unit Price'],
      costo_unitario_inicial: ['Cost', 'Standard Price'],
      stock_actual:           ['On Hand Quantity', 'Quantity On Hand'],
      stock_minimo:           ['Reorder Point'],
    },
    plan_cuentas: {
      codigo:      ['Code', 'Account Code'],
      nombre:      ['Name', 'Account Name'],
      tipo:        ['Type', 'Internal Type'],
      descripcion: ['Note', 'Notes'],
    },
  },

  contpaq: {
    clientes: {
      nombre:            ['Nombre', 'Razón Social', 'Cliente'],
      rnc_cedula:        ['RFC', 'R.F.C.', 'Cédula', 'RNC'],
      email:             ['E-mail', 'Email', 'Correo'],
      telefono:          ['Teléfono', 'Tel.', 'Teléfono 1'],
      direccion:         ['Dirección', 'Calle', 'Domicilio'],
      balance_pendiente: ['Saldo', 'Saldo Actual', 'Saldo Pendiente'],
      limite_credito:    ['Límite de Crédito', 'Crédito'],
    },
    proveedores: {
      nombre:    ['Nombre', 'Razón Social', 'Proveedor'],
      rnc:       ['RFC', 'R.F.C.', 'Cédula', 'RNC'],
      email:     ['E-mail', 'Email', 'Correo'],
      telefono:  ['Teléfono', 'Tel.'],
      direccion: ['Dirección', 'Calle', 'Domicilio'],
      contacto:  ['Contacto', 'Nombre Contacto'],
    },
    productos: {
      codigo:                 ['Código', 'Clave', 'No. Artículo'],
      nombre:                 ['Nombre', 'Descripción', 'Artículo'],
      descripcion:            ['Descripción Larga', 'Notas'],
      unidad:                 ['Unidad', 'UM'],
      precio_venta:           ['Precio Unitario', 'Precio 1', 'Precio Venta'],
      costo_unitario_inicial: ['Costo', 'Costo Unitario'],
      stock_actual:           ['Existencia', 'Stock', 'Cantidad'],
      stock_minimo:           ['Mínimo', 'Stock Mínimo'],
    },
    plan_cuentas: {
      codigo:      ['Cuenta', 'Código', 'No. Cuenta'],
      nombre:      ['Nombre', 'Descripción', 'Denominación'],
      tipo:        ['Tipo', 'Naturaleza'],
      descripcion: ['Notas', 'Descripción'],
    },
  },

  saint: {
    clientes: {
      nombre:            ['Nombre', 'Razón Social', 'Cliente', 'Descripción'],
      rnc_cedula:        ['R.I.F./C.I.', 'R.I.F.', 'RNC', 'Cédula'],
      email:             ['Email', 'Correo', 'E-Mail'],
      telefono:          ['Teléfono', 'Telf.', 'Tel.'],
      direccion:         ['Dirección', 'Domicilio'],
      balance_pendiente: ['Saldo', 'Saldo Actual'],
    },
    proveedores: {
      nombre:    ['Nombre', 'Razón Social', 'Proveedor'],
      rnc:       ['R.I.F.', 'RNC', 'Cédula'],
      email:     ['Email', 'Correo'],
      telefono:  ['Teléfono', 'Telf.'],
      direccion: ['Dirección', 'Domicilio'],
      contacto:  ['Contacto'],
    },
    productos: {
      codigo:                 ['Código', 'Ref.', 'Código Artículo'],
      nombre:                 ['Descripción', 'Artículo', 'Nombre'],
      descripcion:            ['Descripción Larga', 'Notas'],
      unidad:                 ['Unidad', 'UM'],
      precio_venta:           ['Precio', 'Precio Venta', 'PVP'],
      costo_unitario_inicial: ['Costo', 'Precio Costo'],
      stock_actual:           ['Stock Actual', 'Existencia', 'Cantidad'],
      stock_minimo:           ['Stock Mínimo', 'Mínimo'],
    },
    plan_cuentas: {
      codigo:      ['Código', 'Cuenta'],
      nombre:      ['Descripción', 'Nombre'],
      tipo:        ['Tipo', 'Naturaleza'],
      descripcion: ['Notas'],
    },
  },

  excel_generico: {
    clientes: {}, proveedores: {}, productos: {}, plan_cuentas: {},
  },
};

// ─── helper: auto detectar mapeo ─────────────────────────────────────────────

function autoDetectarMapeo(headers, sistema, entidad) {
  const hints = SISTEMAS_MAPEOS[sistema]?.[entidad] || {};
  const headersLow = headers.map(h => h.toLowerCase().trim());
  const mapeo = {};
  const campos = ENTIDADES_CONFIG[entidad]?.campos || [];

  for (const { key } of campos) {
    const candidatos = hints[key] || [];
    const found = candidatos.find(c => headersLow.includes(c.toLowerCase().trim()));
    mapeo[key] = found
      ? headers[headersLow.indexOf(found.toLowerCase().trim())]
      : '';
  }
  return mapeo;
}

// ─── estilos ─────────────────────────────────────────────────────────────────

const sx = {
  page:       { padding: '2rem', maxWidth: 960, margin: '0 auto', fontFamily: 'Inter, sans-serif' },
  titulo:     { fontSize: '1.6rem', fontWeight: 800, color: '#1a1a2e', marginBottom: 4 },
  subtitulo:  { color: '#6c757d', marginBottom: '2rem' },
  card:       { background: '#fff', borderRadius: 12, border: '1px solid #e9ecef', padding: '1.5rem', marginBottom: '1rem' },
  stepBar:    { display: 'flex', gap: '0', marginBottom: '2rem', borderRadius: 8, overflow: 'hidden', border: '1px solid #e9ecef' },
  stepItem:   (active, done) => ({
    flex: 1, padding: '0.75rem', textAlign: 'center', fontSize: '0.82rem', fontWeight: 600,
    background: done ? '#e8f5e9' : active ? '#0066cc' : '#f8f9fa',
    color:      done ? '#2e7d32' : active ? '#fff' : '#adb5bd',
    cursor: done ? 'pointer' : 'default',
  }),
  grid2:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  grid3:      { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  sysCard:    (sel) => ({
    border: `2px solid ${sel ? '#0066cc' : '#e9ecef'}`,
    borderRadius: 10, padding: '1rem', cursor: 'pointer', textAlign: 'center',
    background: sel ? '#e8f0fe' : '#fff', transition: 'all .15s',
  }),
  entCard:    (sel) => ({
    border: `2px solid ${sel ? '#0066cc' : '#e9ecef'}`,
    borderRadius: 10, padding: '1.25rem', cursor: 'pointer',
    background: sel ? '#e8f0fe' : '#fff', transition: 'all .15s',
    display: 'flex', alignItems: 'center', gap: '0.75rem',
  }),
  dropArea:   (drag) => ({
    border: `2px dashed ${drag ? '#0066cc' : '#ced4da'}`,
    borderRadius: 12, padding: '3rem 2rem', textAlign: 'center',
    background: drag ? '#e8f0fe' : '#f8f9fa', cursor: 'pointer', transition: 'all .15s',
  }),
  input:      { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box' },
  select:     { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6, fontSize: '0.875rem', background: '#fff', boxSizing: 'border-box' },
  btnPrim:    { background: '#0066cc', color: '#fff', border: 'none', borderRadius: 8, padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' },
  btnSec:     { background: '#fff', color: '#495057', border: '1px solid #ced4da', borderRadius: 8, padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' },
  btnGreen:   { background: '#28a745', color: '#fff', border: 'none', borderRadius: 8, padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' },
  th:         { padding: '0.5rem 0.75rem', background: '#f1f3f5', fontWeight: 700, fontSize: '0.8rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' },
  td:         { padding: '0.4rem 0.75rem', fontSize: '0.8rem', borderBottom: '1px solid #f1f3f5', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  label:      { fontSize: '0.82rem', fontWeight: 600, color: '#495057', marginBottom: 4, display: 'block' },
  badge:      (c) => ({ display: 'inline-block', padding: '0.3rem 0.7rem', borderRadius: 20, background: c, fontSize: '0.82rem', fontWeight: 700 }),
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────

const PASOS = ['Tipo de Datos', 'Sistema Origen', 'Subir Archivo', 'Mapear Columnas', 'Resultado'];

export default function Migracion() {
  const [moduloActivo, setModuloActivo] = useState(null);
  const [paso, setPaso]           = useState(1);
  const [entidad, setEntidad]     = useState('');
  const [sistema, setSistema]     = useState('');
  const [archivo, setArchivo]     = useState(null);
  const [preview, setPreview]     = useState(null);
  const [mapeo, setMapeo]         = useState({});
  const [loading, setLoading]     = useState(false);
  const [resultado, setResultado] = useState(null);
  const [dragging, setDragging]   = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get('/configuracion').then(r => {
      setModuloActivo(r.data?.modulo_migracion ?? false);
    }).catch(() => setModuloActivo(false));
  }, []);

  // auto-detectar mapeo cuando llega el preview
  useEffect(() => {
    if (!preview || !sistema || !entidad) return;
    setMapeo(autoDetectarMapeo(preview.headers, sistema, entidad));
  }, [preview, sistema, entidad]);

  const irPaso = (n) => { if (n < paso) setPaso(n); };

  const siguientePaso1 = () => {
    if (!entidad) return Swal.fire('Atención', 'Selecciona el tipo de datos a importar', 'warning');
    setPaso(2);
  };
  const siguientePaso2 = () => {
    if (!sistema) return Swal.fire('Atención', 'Selecciona el sistema de origen', 'warning');
    setPaso(3);
  };

  const procesarArchivo = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext))
      return Swal.fire('Formato no válido', 'Solo se aceptan archivos .xlsx, .xls o .csv', 'error');

    setArchivo(file);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('entidad', entidad);
      const res = await api.post('/migracion/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setPaso(4);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo leer el archivo', 'error');
    } finally { setLoading(false); }
  };

  const ejecutarImportacion = async () => {
    const campos = ENTIDADES_CONFIG[entidad]?.campos || [];
    for (const c of campos.filter(x => x.required)) {
      if (!mapeo[c.key])
        return Swal.fire('Campo requerido', `El campo "${c.label}" debe estar mapeado`, 'warning');
    }
    const conf = await Swal.fire({
      title: '¿Ejecutar importación?',
      html: `Se procesarán <b>${preview.total}</b> registros en <b>${ENTIDADES_CONFIG[entidad].label}</b>.<br>Los registros existentes se actualizarán.`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Importar', cancelButtonText: 'Cancelar', confirmButtonColor: '#0066cc',
    });
    if (!conf.isConfirmed) return;

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('entidad', entidad);
      fd.append('mapeo', JSON.stringify(mapeo));
      const res = await api.post('/migracion/importar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResultado(res.data);
      setPaso(5);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al importar', 'error');
    } finally { setLoading(false); }
  };

  const reiniciar = () => {
    setPaso(1); setEntidad(''); setSistema(''); setArchivo(null);
    setPreview(null); setMapeo({}); setResultado(null);
  };

  const descargarPlantilla = () => {
    window.open(`/api/migracion/plantilla/${entidad}`, '_blank');
  };

  if (moduloActivo === null) return <div style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>Cargando...</div>;

  if (!moduloActivo) return (
    <div style={{ padding: '4rem 2rem', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Módulo no disponible</h2>
      <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
        El módulo de Importación / Migración de Datos no está activo en este sistema. Un superadministrador puede activarlo desde Configuración.
      </p>
      <Link to="/configuracion" style={{ background: '#0066cc', color: '#fff', padding: '0.65rem 1.5rem', borderRadius: 8, textDecoration: 'none', fontWeight: 700 }}>
        Ir a Configuración
      </Link>
    </div>
  );

  return (
    <div style={sx.page}>
      <h1 style={sx.titulo}>Importar Datos</h1>
      <p style={sx.subtitulo}>Migra tu información desde QuickBooks, Xero, SAP, Odoo, SAINT y otros sistemas en minutos.</p>

      {/* Barra de pasos */}
      <div style={sx.stepBar}>
        {PASOS.map((label, i) => {
          const n = i + 1;
          const done = n < paso;
          const active = n === paso;
          return (
            <div key={n} style={sx.stepItem(active, done)} onClick={() => irPaso(n)}>
              {done ? '✓ ' : `${n}. `}{label}
            </div>
          );
        })}
      </div>

      {/* ── PASO 1: Tipo de datos ── */}
      {paso === 1 && (
        <div style={sx.card}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.25rem' }}>¿Qué tipo de datos vas a importar?</h2>
          <div style={sx.grid2}>
            {Object.entries(ENTIDADES_CONFIG).map(([key, cfg]) => (
              <div key={key} style={sx.entCard(entidad === key)} onClick={() => setEntidad(key)}>
                <span style={{ fontSize: '2rem' }}>{cfg.icon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{cfg.label}</div>
                  <div style={{ fontSize: '0.82rem', color: '#6c757d' }}>{cfg.campos.length} campos</div>
                </div>
                {entidad === key && <span style={{ marginLeft: 'auto', color: '#0066cc', fontWeight: 900 }}>✓</span>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {entidad && (
              <button onClick={descargarPlantilla} style={sx.btnSec}>
                ⬇ Descargar plantilla {ENTIDADES_CONFIG[entidad].label}
              </button>
            )}
            <button onClick={siguientePaso1} style={{ ...sx.btnPrim, marginLeft: 'auto' }}>
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* ── PASO 2: Sistema origen ── */}
      {paso === 2 && (
        <div style={sx.card}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.25rem' }}>¿Desde qué sistema vas a importar?</h2>
          <p style={{ color: '#6c757d', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
            El sistema detectará automáticamente las columnas. Si no encuentras el tuyo, usa "Excel / CSV genérico" y mapea manualmente.
          </p>
          <div style={sx.grid3}>
            {SISTEMAS.map(s => (
              <div key={s.key} style={sx.sysCard(sistema === s.key)} onClick={() => setSistema(s.key)}>
                <div style={{ fontSize: '1.75rem', marginBottom: 4 }}>{s.logo}</div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{s.label}</div>
                <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{s.pais}</div>
                {sistema === s.key && <div style={{ color: '#0066cc', fontWeight: 900, marginTop: 4 }}>✓ Seleccionado</div>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setPaso(1)} style={sx.btnSec}>← Atrás</button>
            <button onClick={siguientePaso2} style={sx.btnPrim}>Siguiente →</button>
          </div>
        </div>
      )}

      {/* ── PASO 3: Subir archivo ── */}
      {paso === 3 && (
        <div style={sx.card}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>Sube tu archivo</h2>
          <p style={{ color: '#6c757d', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
            Exporta desde <b>{SISTEMAS.find(s => s.key === sistema)?.label}</b> como Excel (.xlsx) o CSV y súbelo aquí.
          </p>

          <div
            style={sx.dropArea(dragging)}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); procesarArchivo(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current.click()}
          >
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📁</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
              {loading ? 'Analizando archivo...' : 'Arrastra tu archivo aquí o haz clic para seleccionar'}
            </div>
            <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>Soporta .xlsx, .xls y .csv — máximo 20 MB</div>
            {archivo && <div style={{ marginTop: 12, color: '#0066cc', fontWeight: 600 }}>📄 {archivo.name}</div>}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
            onChange={e => procesarArchivo(e.target.files[0])} />

          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setPaso(2)} style={sx.btnSec}>← Atrás</button>
          </div>
        </div>
      )}

      {/* ── PASO 4: Mapear columnas ── */}
      {paso === 4 && preview && (
        <div>
          <div style={sx.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Mapear columnas</h2>
                <p style={{ color: '#6c757d', fontSize: '0.85rem', margin: '4px 0 0' }}>
                  Archivo: <b>{archivo?.name}</b> — <b>{preview.total}</b> registros detectados
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ ...sx.badge('#e8f0fe'), color: '#0066cc' }}>
                  {SISTEMAS.find(s => s.key === sistema)?.label}
                </span>
                <span style={{ ...sx.badge('#f0fdf4'), color: '#166534' }}>
                  {ENTIDADES_CONFIG[entidad].icon} {ENTIDADES_CONFIG[entidad].label}
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {(ENTIDADES_CONFIG[entidad]?.campos || []).map(campo => (
                <div key={campo.key}>
                  <label style={sx.label}>
                    {campo.label} {campo.required && <span style={{ color: '#dc3545' }}>*</span>}
                    {mapeo[campo.key] && mapeo[campo.key] !== '' && (
                      <span style={{ color: '#28a745', marginLeft: 6 }}>✓ auto</span>
                    )}
                  </label>
                  <select
                    value={mapeo[campo.key] || ''}
                    onChange={e => setMapeo(m => ({ ...m, [campo.key]: e.target.value }))}
                    style={{ ...sx.select, borderColor: campo.required && !mapeo[campo.key] ? '#dc3545' : '#ced4da' }}
                  >
                    <option value="">— no importar este campo —</option>
                    {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {/* Vista previa tabla */}
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.75rem' }}>Vista previa (primeras 8 filas)</h3>
            <div style={{ overflowX: 'auto', border: '1px solid #e9ecef', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    {(ENTIDADES_CONFIG[entidad]?.campos || []).filter(c => mapeo[c.key]).map(c => (
                      <th key={c.key} style={sx.th}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.muestra.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                      {(ENTIDADES_CONFIG[entidad]?.campos || []).filter(c => mapeo[c.key]).map(c => (
                        <td key={c.key} style={sx.td} title={String(row[mapeo[c.key]] ?? '')}>
                          {String(row[mapeo[c.key]] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setPaso(3)} style={sx.btnSec}>← Atrás</button>
            <button onClick={ejecutarImportacion} style={sx.btnGreen} disabled={loading}>
              {loading ? 'Importando...' : `▶ Importar ${preview.total} registros`}
            </button>
          </div>
        </div>
      )}

      {/* ── PASO 5: Resultado ── */}
      {paso === 5 && resultado && (
        <div style={sx.card}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '1.5rem', textAlign: 'center' }}>
            Importación completada
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#166534' }}>{resultado.creados}</div>
              <div style={{ fontSize: '0.9rem', color: '#166534', fontWeight: 600 }}>Registros creados</div>
            </div>
            <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#1d4ed8' }}>{resultado.actualizados}</div>
              <div style={{ fontSize: '0.9rem', color: '#1d4ed8', fontWeight: 600 }}>Registros actualizados</div>
            </div>
            <div style={{ background: resultado.errores > 0 ? '#fef2f2' : '#f8f9fa', border: `1px solid ${resultado.errores > 0 ? '#fca5a5' : '#e9ecef'}`, borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: resultado.errores > 0 ? '#991b1b' : '#6c757d' }}>{resultado.errores}</div>
              <div style={{ fontSize: '0.9rem', color: resultado.errores > 0 ? '#991b1b' : '#6c757d', fontWeight: 600 }}>Con errores</div>
            </div>
          </div>

          {resultado.detalle_errores?.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: '0.5rem' }}>Detalle de errores (primeros 30):</div>
              {resultado.detalle_errores.map((e, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: '#7f1d1d', padding: '2px 0' }}>
                  Fila {e.fila}: {e.error}
                </div>
              ))}
            </div>
          )}

          <div style={{ textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={reiniciar} style={sx.btnPrim}>↩ Importar otra entidad</button>
            <button onClick={() => { setArchivo(null); setPreview(null); setResultado(null); setPaso(3); }} style={sx.btnSec}>
              Repetir con otro archivo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
